/**
 * O's merchflow Q&A: read-only answers about anything in a merchflow, asked
 * from any merchflow page. It changes nothing; where something needs doing it
 * names the place, and `goTo` links there.
 */
export const RoutePostMerchflowQa = "/merchflows/:merchflowId/qa";
/** Identifies one server answer so feedback can be joined to its telemetry event. */
export const MerchflowQaAnswerIdHeader = "x-o-answer-id";

/** The page the question was asked on, so "this" and "here" mean something. */
export type QaPage =
  | { kind: "merchflow" }
  | { kind: "base_pog"; basePogId: number }
  | { kind: "cdt_studio"; basePogId?: number }
  | { kind: "stores"; storePogTriggerId?: number }
  | { kind: "store"; storeCode: string; storePogTriggerId?: number }
  | { kind: "store_pog"; storePogId: number };

/** Where to act on what an answer names. `data` has no link: it's the data feed. */
export type QaPlace = { label: string } & (
  | { kind: "cdt_studio"; basePogId: number }
  | { kind: "base_pog"; basePogId: number }
  | { kind: "rules"; basePogId: number }
  | { kind: "stores"; storePogTriggerId?: number }
  | { kind: "store_pog"; storePogId: number; storeCode: string }
  | { kind: "data" }
);

export interface QaTurn {
  role: "user" | "o";
  text: string;
}

export interface PayloadPostMerchflowQa {
  page: QaPage;
  text: string;
  /** The recent turns, oldest first, at most 8. */
  history?: QaTurn[];
}

export interface ResponsePostMerchflowQa {
  /** At most three short sentences. */
  answer: string;
  /** Where to act on what the answer names, at most two. Empty when nothing needs doing. */
  goTo: QaPlace[];
  /** Follow-up questions in the user's words, at most three. */
  chips: string[];
  /** A store run of this merchflow is still generating. */
  running: boolean;
}

/** 503: no model is configured, or it failed or ran past the answer deadline (30 s). */
export interface ErrorPostMerchflowQaUnavailable {
  code: "O_UNAVAILABLE";
  message: string;
}

/** 429: this merchant has used its current Q&A allowance; retry after the header's delay. */
export interface ErrorPostMerchflowQaRateLimited {
  code: "O_QA_RATE_LIMITED";
  message: string;
  retryAfterSeconds: number;
}

/** Thumbs up or down on one of O's answers, so wrong and weak answers come back to us. Logged, never shown to O. */
export const RoutePostMerchflowQaFeedback = "/merchflows/:merchflowId/qa/feedback";

export interface PayloadPostMerchflowQaFeedback {
  page: QaPage;
  question: string;
  answer: string;
  /** True: the answer was right and useful. */
  helpful: boolean;
  /** What was wrong or missing, in the user's words. At most 500 characters. */
  comment?: string;
  /** The answer's server-issued id, returned in MerchflowQaAnswerIdHeader. */
  answerId?: string;
}
